import React from 'react';
import cs from 'classnames';
import { Form, Formik } from 'formik';
import isEqual from 'lodash/isEqual';
import * as yup from 'yup';
import { slugs, useRentalSteps } from '../../app/steps';
import { LoadingLabel } from '../../../shared/components/Loading';
import { RadioField } from 'shared/formik/RadioFieldGroup';
import { EnumOptions, SelectFieldGroup } from 'shared/formik/SelectFieldGroup';
// import { TextAreaGroup } from '../../components/formik/TextAreaGroup';
import { TextFieldGroup } from 'shared/formik/TextFieldGroup';
import { MutationErrors } from '../../../shared/components/MutationErrors';
import { PageContainer } from 'shared/components/PageContainer';
import { RentalPageTitleHelmet } from 'rental/components/PageTitleHelmet';
import { SubmitActions } from '../../../shared/components/SubmitActions';
import { NextLink, PrevLink, SubmitButtons } from 'shared/components/SubmitButtons';
import { PageHeading } from 'shared/components/typography';
import {
  ApplicationAddressFieldsFragment,
  ApplicationDeliveryAddressFieldsFragment,
  ApplicationLivingSituationFieldsFragment,
  App_ApplicationFragment,
  App_PortalFragment, DeliveryAddressType,
  ErrorType,
  useAddressQuery,
  HomeTypeSource,
  useUpdateApplicationAddressMutation, useUpdateApplicationDealerMutation, useUpdateApplicationDeliveryAddressMutation
} from '../../generated.graphql';
import { getSubmitActionsProps } from 'shared/forms/helpers';
import { regroup } from 'shared/utils/regroup';
import { RentalStepProps } from 'rental/app/steps';
import { yupRequiredMsg } from '../../../shared/yup';
import { defaultAddressSegments } from './segments';
import { AddressField, AddressComponentType } from './AddressField';
import { StateOptions } from './utils';

interface Props extends RentalStepProps {
  portal: App_PortalFragment
  application: App_ApplicationFragment;
}


/**
 * Values contain flattened address fields from the graph
 */
interface Values extends
  Omit<ApplicationAddressFieldsFragment, "__typename" | "residentialAddress">,
  Omit<ApplicationDeliveryAddressFieldsFragment, "__typename" | "dealer" | "deliveryAddress">,
  Omit<ApplicationLivingSituationFieldsFragment, "__typename" | "homeType" | 'homeDurationYears' | 'homeDurationMonths'>
{
  homeType: HomeTypeSource | '';
  homeDurationYears: string | '';
  homeDurationMonths: string | '';

  residential_addressComponent: AddressComponentType;
  residential_fullAddress: string | null;
  residential_street: string | null;
  residential_suburb: string | null;
  residential_state: string | null;
  residential_postcode: string | null;

  deliveryAddressType: DeliveryAddressType;
  dealer: string | null;
  delivery_addressComponent: AddressComponentType;
  delivery_fullAddress: string | null;
  delivery_street: string | null;
  delivery_suburb: string | null;
  delivery_state: string | null;
  delivery_postcode: string | null;
}

/**
 * Remaps errors from an address mutation to an expected formik state.
 * e.g. fields { fullAddress, street } -> { residential_fullAddress, residential_street }
 * @param prefix e.g. 'residential_'
 */
function remapAddressErrors(prefix: string, errors?: readonly (ErrorType | null | undefined)[] | null): ErrorType[] {
  //return errors?.map((err) => (err && `${err.field}: ${err.messages.join(' ')}`) ?? '').join(' ') ?? '';
  return errors?.filter<ErrorType>((err): err is ErrorType => (err as ErrorType).field !== undefined)
    .map((err: ErrorType) => ({
      field: `${prefix}${err.field}`,
      messages: err.messages
    })) ?? [];
}

function extractAddress(prefix: string, value: Values & { [key: string]: any }) {
  return Object.keys(defaultAddressSegments).reduce<{ [key: string]: any }>((obj, name) => {
    obj[name] = value[`${prefix}${name}`];
    return obj;
  }, {});
}

export const Address = (props: Props) => {
  const steps = useRentalSteps({slug: slugs.address});
  const [result,] = useAddressQuery({variables: {portalSlug: props.application.portal.slug}});

  const [, executeUpdateAddressMutation] = useUpdateApplicationAddressMutation();
  const [, executeUpdateDeliveryAddressMutation] = useUpdateApplicationDeliveryAddressMutation();
  const [, executeUpdateDealerMutation] = useUpdateApplicationDealerMutation();

  const application = result?.data?.portal?.myApplication;
  const address = application?.address;
  const livingSituation = result?.data?.portal?.myApplication?.livingSituation;
  const dealers = props.portal.dealers ?? [];

  if (!address) {
    return (
      <PageContainer>
        <LoadingLabel />
        <SubmitButtons
          left={steps.prev && <PrevLink to={steps.prev.link}/>}
          right={steps.next && <NextLink to={steps.next.link}/>}
        />
      </PageContainer>
    );
  }

  const residentialPrefix = 'residential_';
  const deliveryPrefix = 'delivery_';

  // configure custom validation schema rules
  const getPrefixedAddressSchema = (prefix: string) => ({
    [`${prefix}street`]: yup.string().nullable().when(`${prefix}addressComponent`, {
      is: 'custom',
      then: yup.string().nullable().min(5, 'Must be a full street address.  Eg. 123 Example Road').max(255).required(yupRequiredMsg)
    }),
    [`${prefix}suburb`]: yup.string().nullable().when(`${prefix}addressComponent`, {
      is: 'custom',
      then: yup.string().nullable().max(100).required(yupRequiredMsg)
    }),
    [`${prefix}state`]: yup.string().nullable().when(`${prefix}addressComponent`, {
      is: 'custom',
      then: yup.string().nullable().oneOf(StateOptions.map(o => o[0])).required(yupRequiredMsg)
    }),
    [`${prefix}postcode`]: yup.string().nullable().when(`${prefix}addressComponent`, {
      is: 'custom',
      then: yup.string().nullable().matches(/\d{4}/, 'Must be a 4 digit postcode').required(yupRequiredMsg)
    }),
  });

  const addressValidationSchema = yup.object().shape({
    homeType: yup.string().required(yupRequiredMsg),
    homeDurationYears: yup.number().required(yupRequiredMsg).typeError('Must be a number').min(0, 'Must be zero or greater').nullable(),
    homeDurationMonths: yup.number().typeError('Must be a number').min(0, 'Must be zero or greater').max(11, 'Must be less than 12').nullable(),
    residential_fullAddress: yup.string().nullable().required(yupRequiredMsg),
    ...getPrefixedAddressSchema(residentialPrefix),
    deliveryAddressType: yup.mixed<DeliveryAddressType>().oneOf(Object.values(DeliveryAddressType)),
    dealer: yup.string().nullable()
      .when("deliveryAddressType", {
        is: DeliveryAddressType.Dealer,
        then: yup.string().required(yupRequiredMsg).typeError(yupRequiredMsg)
      }),
    delivery_fullAddress: yup.string().nullable()
      .when("deliveryAddressType", {
        is: DeliveryAddressType.Custom,
        then: yup.string().nullable().required(yupRequiredMsg)
      }),
    ...getPrefixedAddressSchema(deliveryPrefix),
    // deliveryInstructions: yup.string().max(70, "Delivery instructions must be less than 70 characters."), // actually 75 chars, but ensures it's more likely to be split into 3x 25char lines cleanly
  });

  // configure delivery address options based on portal/instore/etc
  const deliveryAddressTypes: { [key in DeliveryAddressType]: string | null } = {
    DEALER: null,
    RESIDENTIAL: null,
    CUSTOM: null,
  };

  if (!props.application.inStore) {
    if (props.portal.requireDealer && dealers.length > 0) {
      deliveryAddressTypes.DEALER = 'Pick up from my local store';
    }

    deliveryAddressTypes.RESIDENTIAL = 'Delivery address is the same as my residential address';
    deliveryAddressTypes.CUSTOM = 'Deliver to a different address';
  }

  return (
    <PageContainer>
      <RentalPageTitleHelmet>Address</RentalPageTitleHelmet>
      <PageHeading subtitle="What is your current address?">My Address</PageHeading>
      <Formik<Values>
        initialValues={{
          ...livingSituation,
          homeType: livingSituation?.homeType || '',
          // Need to be converted to a string to be used by <input type='text' />
          homeDurationYears: livingSituation?.homeDurationYears?.toString() || '',
          homeDurationMonths: livingSituation?.homeDurationMonths?.toString() || '',

          residential_addressComponent: !!address.residentialAddress?.fullAddress ? 'label' : 'typeahead',
          residential_fullAddress: address.residentialAddress?.fullAddress ?? null,
          residential_street: null,
          residential_suburb: null,
          residential_state: null,
          residential_postcode: null,
        
          deliveryAddressType: application?.address.deliveryAddressType ?? DeliveryAddressType.Residential,
          dealer: application?.address.dealer?.id ?? null,
          delivery_addressComponent: !!address.deliveryAddress?.fullAddress ? 'label' : 'typeahead',
          delivery_fullAddress: address.deliveryAddress?.fullAddress ?? null,
          delivery_street: null,
          delivery_suburb: null,
          delivery_state: null,
          delivery_postcode: null,
          //deliveryInstructions: address.deliveryInstructions,
        }}
        validationSchema={addressValidationSchema}
        onSubmit={async (data, helpers) => {
          if (isEqual(data, { ...data, ...address })) {
            // no change since last submit
            props.onSubmitSuccessful();
          } else {
            helpers.setSubmitting(true);

            const baseInput = {
              portalSlug: props.application.portal.slug,
            };

            const r = await executeUpdateAddressMutation({
              residential: {
                ...baseInput,
                fullAddress: data.residential_fullAddress ?? '',
                ...(data.residential_addressComponent === 'custom' ? extractAddress(residentialPrefix, data) : {}),
              },
              situation: {
                ...baseInput,
                homeType: data.homeType || HomeTypeSource.Own,
                homeDurationYears: parseInt(data.homeDurationYears ?? '') ?? 0,
                homeDurationMonths: parseInt(data.homeDurationMonths ?? '') ?? null,
              },
            });
            const addressIsValid = !r.error && r.data?.setResidentialAddress?.application != null && r.data?.setLivingSituation?.application != null;
            if (!addressIsValid) {
              //helpers.setFieldError('residentialAddress', graphQlErrorToFormikError(r.data?.setResidentialAddress?.errors));
              helpers.setStatus(remapAddressErrors(residentialPrefix, r.data?.setResidentialAddress?.errors) || null);
              helpers.setStatus(r.data?.setLivingSituation?.errors || null);
            }

            let deliveryIsValid = false;
            if (data.deliveryAddressType === DeliveryAddressType.Dealer) {
              const input = {
                dealer: {
                  ...baseInput,
                  dealer: data.dealer ?? '',
                },
                portalSlug: props.application.portal.slug
              };
              const r2 = await executeUpdateDealerMutation(input);
              deliveryIsValid = !r2.error && r2.data?.setDeliveryDealer?.application != null;
              if (!deliveryIsValid) {
                // https://stackoverflow.com/a/54328567
                //helpers.setFieldError('dealer', graphQlErrorToFormikError(r2.data?.setDeliveryDealer?.errors));
                helpers.setStatus(r2.data?.setDeliveryDealer?.errors || null);
              }
            } else {
              const input = {
                delivery: {
                  ...baseInput,
                  deliveryUseHomeAddress: data.deliveryAddressType === DeliveryAddressType.Residential,
                  fullAddress: data.delivery_fullAddress ?? '',
                  deliveryInstructions: '', //data.deliveryInstructions,
                  ...(data.delivery_addressComponent === 'custom' ? extractAddress(deliveryPrefix, data) : {}),
                },
                portalSlug: props.application.portal.slug
              };
              const r2 = await executeUpdateDeliveryAddressMutation(input);
              deliveryIsValid = !r2.error && r2.data?.setDeliveryAddress?.application != null;
              if (!deliveryIsValid) {
                //helpers.setFieldError('deliveryAddress', graphQlErrorToFormikError(r2.data?.setDeliveryAddress?.errors));
                helpers.setStatus(remapAddressErrors(deliveryPrefix, r2.data?.setDeliveryAddress?.errors) || null);
              }
            }

            helpers.setSubmitting(false);
            if (addressIsValid && deliveryIsValid) {
              props.onSubmitSuccessful();
            } else {
              props.onSubmitFailure();
            }
          }
        }}
      >
        {(formik) => (
        <Form noValidate>
          {formik.status && <MutationErrors errors={formik.status}/>}

          <AddressField
            fieldPrefix={residentialPrefix}
            addressName="residential"
            placeholderText="Start typing your residential address..."
          />

          <div className="form-row">
            <div className="col-md-4 col-lg-3">
              <SelectFieldGroup name="homeType" label="Living situation" blank={true} custom >
                <EnumOptions enum={result.data?.homeTypeSourceEnum}/>
              </SelectFieldGroup>
            </div>
            <div className="col-md-4 col-lg-3 text-md-right">
              <label className="pt-sm-1 mt-sm-2">How long have you lived here?</label>
            </div>
            <div className="col-6 col-md-2 col-lg-3">
              <TextFieldGroup name="homeDurationYears" label="Years" pattern="[0-9]*" inputMode="numeric" />
            </div>
            <div className="col-6 col-md-2 col-lg-3">
              <TextFieldGroup name="homeDurationMonths" label="Months" pattern="[0-9]*" inputMode="numeric" />
            </div>
          </div>

          {Object.keys(deliveryAddressTypes).some(k => deliveryAddressTypes[k as DeliveryAddressType]) && (
            <h3 className="mt-3">Delivery</h3>
          )}

          {deliveryAddressTypes.DEALER && (
            <>
              <RadioField
                name="deliveryAddressType"
                className={cs(formik.values.deliveryAddressType !== DeliveryAddressType.Dealer && "mb-1")}
                label={deliveryAddressTypes.DEALER}
                option={DeliveryAddressType.Dealer}
                custom
              />
              {formik.values.deliveryAddressType === DeliveryAddressType.Dealer && ( 
                <SelectFieldGroup name="dealer" label="Select a dealer to pick-up from" blank custom>
                  {Object.values(regroup(dealers, item => item.state).map(group =>
                    <optgroup key={group.grouper} label={group.grouper}>
                      {group.list.map(d => 
                        <option key={d.id} value={d.id}>{d.name} &ndash; {d.city}</option>
                      )}
                    </optgroup>
                  ))}
                </SelectFieldGroup>
              )}
            </>
          )}

          {deliveryAddressTypes.RESIDENTIAL && (
            <RadioField
              name="deliveryAddressType"
              className="mb-1"
              label={deliveryAddressTypes.RESIDENTIAL}
              option={DeliveryAddressType.Residential}
              custom
            />
          )}

          {deliveryAddressTypes.CUSTOM && (
            <>
              <RadioField
                name="deliveryAddressType"
                className={cs(formik.values.deliveryAddressType !== DeliveryAddressType.Custom && "mb-1")}
                label={deliveryAddressTypes.CUSTOM}
                option={DeliveryAddressType.Custom}
                custom
              />
              {formik.values.deliveryAddressType === DeliveryAddressType.Custom && (
                <AddressField
                  fieldPrefix={deliveryPrefix}
                  addressName="delivery"
                  placeholderText="Start typing your delivery address..."
                />
              )}
            </>
          )}

          {/*formik.values.deliveryAddressType !== DeliveryAddressType.Dealer && (
            <TextAreaGroup className={cs(formik.values.deliveryAddressType === DeliveryAddressType.Residential && 'mt-3')} name="deliveryInstructions" label="Delivery instructions (optional)" rows={3} />
          )*/}

          <SubmitActions {...getSubmitActionsProps(formik, props)} />
        </Form>
        )}
      </Formik>
    </PageContainer>
  );
}
